import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import { useTheme } from "@mui/material";
import PropTypes from "prop-types";

HighchartsHeatmap(Highcharts);

const tooltipFormatter = function (heatMapData) {
  return `
    ${heatMapData.companyName[this.point.y]}
    <br/>
    ${heatMapData.formattedDates[this.point.x]}: <b>${this.point.value}</b>`;
};

export default function HeatMapByPartner({ heatMapData, onPointSelect }) {
  const theme = useTheme();
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const resizeTimerRef = useRef(null);
  const [options, setOptions] = useState({
    chart: {
      type: "heatmap",
      marginTop: 40,
      marginBottom: 60,
      animation: false,
      backgroundColor: theme.palette.demo.heatmapbackgroundcolor
    },
    title: {
      text: null
    },
    xAxis: {
      categories: [],
      labels: {
        rotation: 0,
        style: {
          color: theme.palette.demo.heatmapcolor,
          fontSize: "10px"
        }
      }
    },
    yAxis: {
      categories: [],
      labels: {
        style: {
          color: theme.palette.demo.heatmapcolor,
          fontSize: "10px"
        }
      },
      title: null
    },
    colorAxis: {
      min: 0,
      max: 40,
      reversed: false,
      stops: [
        [0, "#00437F"],
        [0.33, "#0086FF"],
        [0.56, "#83C5FF"],
        [0.68, "#DCDEDF"],
        [0.8, "#DCDEDF"],
        [1, "#F4F1F1"]
      ]
    },
    legend: {
      align: "right",
      layout: "vertical",
      margin: 0,
      verticalAlign: "top",
      y: 25,
      symbolHeight: 280
    },
    series: [],
    credits: {
      enabled: false
    },
    plotOptions: {
      series: {
        animation: false
      }
    }
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const { height, width } = entries[0].contentRect;
      clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        setContainerHeight(height);
        setContainerWidth(width);
        // Tell Highcharts to remeasure its container — without this it can
        // render at the wrong width after layout shifts (e.g. sidebar open/close)
        chartRef.current?.chart?.reflow();
      }, 150);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      clearTimeout(resizeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (heatMapData) {
      const totalRows = heatMapData?.companyName?.length || 0;
      const totalCols = heatMapData?.formattedDates?.length || 0;

      const chartHeight = containerHeight || 450;
      const chartWidth = containerWidth || 900;
      const MIN_CELL_HEIGHT = 35;
      const PREFERRED_CELL_HEIGHT = 45;
      const MIN_CELL_WIDTH = 90;
      const PREFERRED_CELL_WIDTH = 120;

      const rowsByPreferred = Math.floor(chartHeight / PREFERRED_CELL_HEIGHT);
      const rowsByMinHeight = Math.floor(chartHeight / MIN_CELL_HEIGHT);
      const visibleRows = Math.min(Math.max(rowsByPreferred, 1), rowsByMinHeight, totalRows);

      const colsByPreferred = Math.floor(chartWidth / PREFERRED_CELL_WIDTH);
      const colsByMinWidth = Math.floor(chartWidth / MIN_CELL_WIDTH);
      const visibleCols = Math.min(Math.max(colsByPreferred, 1), colsByMinWidth, totalCols);

      setOptions(options => ({
        ...options,
        chart: {
          ...options.chart,
          height: chartHeight,
          animation: false,
          backgroundColor: theme.palette.demo.heatmapbackgroundcolor
        },
        navigator: {
          enabled: false
        },
        xAxis: {
          ...options.xAxis,
          categories: heatMapData?.formattedDates,
          min: 0,
          max: Math.min(visibleCols - 1, totalCols - 1),
          startOnTick: false,
          endOnTick: false,
          scrollbar: {
            enabled: totalCols > visibleCols,
            liveRedraw: true
          },
          labels: {
            rotation: 0,
            style: {
              color: theme.palette.demo.heatmapcolor,
              fontSize: "10px"
            }
          }
        },
        yAxis: {
          ...options.yAxis,
          categories: heatMapData?.companyName,
          min: 0,
          max: Math.min(visibleRows - 1, totalRows - 1),
          startOnTick: false,
          endOnTick: false,
          scrollbar: {
            enabled: totalRows > visibleRows,
            liveRedraw: true
          },
          labels: {
            style: {
              color: theme.palette.demo.heatmapcolor,
              fontSize: "10px"
            }
          }
        },
        tooltip: {
          formatter: function () {
            return tooltipFormatter.call(this, heatMapData);
          }
        },
        series: [
          {
            type: "heatmap",
            borderWidth: 1,
            borderColor: "#fff",
            data: heatMapData?.seriesData,
            turboThreshold: 0,
            dataLabels: {
              enabled: true,
              style: {
                textOutline: "none"
              }
            },
            allowPointSelect: true,
            point: {
              events: {
                select: function () {
                  onPointSelect(this);
                }
              }
            }
          }
        ]
      }));
    }
  }, [heatMapData, theme, containerHeight, containerWidth]);

  return (
    <div ref={containerRef} style={{ height: "100%", width: "100%" }}>
      <HighchartsReact highcharts={Highcharts} ref={chartRef} options={options} immutable={false} />
    </div>
  );
}

HeatMapByPartner.propTypes = {
  heatMapData: PropTypes.shape({
    formattedDates: PropTypes.arrayOf(PropTypes.string).isRequired,
    companyName: PropTypes.arrayOf(PropTypes.string).isRequired,
    seriesData: PropTypes.arrayOf(
      PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        value: PropTypes.number.isRequired
      })
    ).isRequired
  }).isRequired,
  onPointSelect: PropTypes.func.isRequired
};
